import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import FeedbackPopup from '../components/FeedbackPopup';
import { SEEDED_WAITLIST_COUNT, BACKEND_URL } from '../constants';
import { 
  Shield, 
  TrendingUp, 
  Bell, 
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  DollarSign,
  Eye,
  Activity,
  BarChart3,
  Calendar,
  Zap,
  Lock,
  CreditCard,
  Target,
  RefreshCw,
  Layers
} from 'lucide-react';
import '../styles/LandingPage.css';
import '../styles/WatchdogPage.css';

const FIGMA_DEMO_URL = 'https://www.figma.com/proto/VFZ9YuB9BbNNhyLgHh9T3j/Money-Sentinel?node-id=0-1&t=bk5flBmOPkVTsnGl-1';

const WatchdogPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [priorityProblem, setPriorityProblem] = useState('');
  const [otherProblem, setOtherProblem] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(SEEDED_WAITLIST_COUNT);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);

  // Fetch waitlist count
  useEffect(() => {
    const fetchWaitlistCount = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/waitlist/count`);
        const data = await response.json();
        setWaitlistCount(SEEDED_WAITLIST_COUNT + data.count);
      } catch (error) {
        // Keep default value
      }
    };
    
    fetchWaitlistCount();
  }, []);

  // Handle demo click
  const handleDemoClick = useCallback((url) => {
    localStorage.setItem('demoOpened', 'true');
    
    if (window.posthog) {
      window.posthog.capture('demo_clicked_watchdog', {
        source_page: '/watchdog'
      });
    }
    
    window.open(url, '_blank');
  }, []);

  // Check for demo feedback
  useEffect(() => {
    let timeoutId;
    let intervalId;
    
    const showPopupIfNeeded = (source = 'unknown') => {
      const demoOpened = localStorage.getItem('demoOpened');
      
      if (demoOpened === 'true' && !showFeedbackPopup) {
        if (timeoutId) clearTimeout(timeoutId);
        
        timeoutId = setTimeout(() => {
          setShowFeedbackPopup(true);
          
          if (window.posthog) {
            window.posthog.capture('demo_feedback_popup_shown', {
              trigger_source: source,
              source_page: '/watchdog'
            });
          }
          
          localStorage.removeItem('demoOpened');
        }, 2000);
      }
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        showPopupIfNeeded('visibilitychange');
      }
    };

    const handleFocus = () => {
      showPopupIfNeeded('focus');
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    showPopupIfNeeded('mount');
    
    intervalId = setInterval(() => {
      if (!document.hidden) {
        const demoOpened = localStorage.getItem('demoOpened');
        if (demoOpened === 'true' && !showFeedbackPopup) {
          showPopupIfNeeded('interval');
        }
      }
    }, 2000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [showFeedbackPopup]);

  const handleFeedbackSubmit = useCallback(async (feedbackData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit feedback');
      }
      
      if (window.posthog) {
        window.posthog.capture('feedback_submitted_success', {
          source_page: '/watchdog'
        });
      }
    } catch (error) {
      if (window.posthog) {
        window.posthog.capture('feedback_submission_failed', {
          error: error.message,
          source_page: '/watchdog'
        });
      }
    }
  }, []);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    
    const submissionData = {
      email,
      country: country || null,
      priority_problem: priorityProblem === 'other' ? otherProblem : priorityProblem,
      source_page: '/watchdog',
      source_campaign: 'financial_watchdog',
      signup_url: window.location.href,
      referrer_url: document.referrer || null,
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      utm_content: urlParams.get('utm_content') || null,
      timestamp: new Date().toISOString(),
    };
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to join waitlist');
      }

      setIsSubmitting(false);
      setWaitlistCount(prev => prev + 1);
      
      if (window.posthog) {
        window.posthog.capture('waitlist_submitted_watchdog', {
          source_page: '/watchdog',
          source_campaign: 'financial_watchdog',
          priority_problem: submissionData.priority_problem,
          has_utm: !!(urlParams.get('utm_source') || urlParams.get('utm_campaign'))
        });
      }
      
      // Redirect to survey
      navigate('/survey', {
        state: {
          email: submissionData.email,
          source_page: '/watchdog',
          source_campaign: 'financial_watchdog',
          priority_problem: submissionData.priority_problem
        }
      });
      
    } catch (error) {
      const pendingSubmissions = JSON.parse(localStorage.getItem('pendingWaitlist') || '[]');
      pendingSubmissions.push(submissionData);
      localStorage.setItem('pendingWaitlist', JSON.stringify(pendingSubmissions));
      
      setIsSubmitting(false);
      
      if (window.posthog) {
        window.posthog.capture('waitlist_joined_offline', {
          saved_to_backup: true,
          source_page: '/watchdog',
          priority_problem: submissionData.priority_problem,
        });
      }
      
      // Redirect to survey even for offline signups
      navigate('/survey', {
        state: {
          email: submissionData.email,
          source_page: '/watchdog',
          source_campaign: 'financial_watchdog',
          priority_problem: submissionData.priority_problem
        }
      });
    }
  };

  // Track page view
  useEffect(() => {
    if (window.posthog) {
      window.posthog.capture('page_view_watchdog', {
        page_path: '/watchdog',
        source_campaign: 'financial_watchdog'
      });
    }
  }, []);

  return (
    <div className="watchdog-page">
      {/* Hero Section */}
      <section className="watchdog-hero-section">
        <div className="watchdog-hero-container">
          <div className="watchdog-hero-content">
            <h1 className="watchdog-hero-title">
              Your Proactive Financial Watchdog
            </h1>
            <p className="watchdog-hero-subtitle">
              MySentinel continuously monitors your financial activity to detect hidden money leaks, 
              rising bills, unnecessary interest, credit utilization risks, and forgotten subscriptions 
              before they become costly.
            </p>
            <p className="watchdog-hero-support">
              Not a dashboard. A proactive assistant that watches for financial problems and tells you what action to take.
            </p>
            
            <div className="watchdog-hero-ctas">
              <Button 
                onClick={() => document.getElementById('watchdog-waitlist')?.scrollIntoView({ behavior: 'smooth' })}
                className="watchdog-cta-primary"
              >
                Join Early Access
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={() => handleDemoClick(FIGMA_DEMO_URL)}
                variant="outline"
                className="watchdog-cta-secondary"
              >
                Explore Interactive Demo
              </Button>
            </div>
          </div>
          
          {/* Hero Dashboard Visual */}
          <div className="watchdog-dashboard-demo">
            <div className="dashboard-label">
              <Layers className="dashboard-label-icon" />
              <span>360° Money Leak Detection</span>
            </div>
            
            <div className="dashboard-alerts">
              <Card className="dashboard-alert-card alert-1">
                <CardContent className="dashboard-alert-content">
                  <div className="alert-mini-header">
                    <Bell className="alert-mini-icon" />
                    <span className="alert-mini-badge">Free Trial Ending Soon</span>
                  </div>
                  <h4 className="alert-mini-title">Adobe Trial renews in 2 days</h4>
                  <p className="alert-mini-detail">Next charge: £29.99</p>
                </CardContent>
              </Card>

              <Card className="dashboard-alert-card alert-2">
                <CardContent className="dashboard-alert-content">
                  <div className="alert-mini-header">
                    <TrendingUp className="alert-mini-icon" />
                    <span className="alert-mini-badge">Bill Increase Detected</span>
                  </div>
                  <h4 className="alert-mini-title">Broadband increased from £39 to £52</h4>
                </CardContent>
              </Card>

              <Card className="dashboard-alert-card alert-3">
                <CardContent className="dashboard-alert-content">
                  <div className="alert-mini-header">
                    <BarChart3 className="alert-mini-icon" />
                    <span className="alert-mini-badge">Credit Utilization Alert</span>
                  </div>
                  <h4 className="alert-mini-title">Card A is at 42% utilization</h4>
                  <p className="alert-mini-detail">Card B is at 8%</p>
                </CardContent>
              </Card>

              <Card className="dashboard-alert-card alert-4">
                <CardContent className="dashboard-alert-content">
                  <div className="alert-mini-header">
                    <AlertCircle className="alert-mini-icon" />
                    <span className="alert-mini-badge">Interest Leakage Detected</span>
                  </div>
                  <h4 className="alert-mini-title">Card A carried balance for 2 months</h4>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="watchdog-problem-section">
        <div className="section-container">
          <h2 className="section-title">Money Leaks Rarely Come From One Place</h2>
          <p className="section-description-large">
            Most people do not lose money from one obvious mistake.
          </p>
          
          <p className="section-description">
            Money quietly leaks through many small places:
          </p>
          
          <div className="leak-sources-grid">
            <div className="leak-source">
              <CheckCircle2 className="leak-source-icon" />
              <span>Forgotten subscriptions</span>
            </div>
            <div className="leak-source">
              <CheckCircle2 className="leak-source-icon" />
              <span>Free trials turning into paid plans</span>
            </div>
            <div className="leak-source">
              <CheckCircle2 className="leak-source-icon" />
              <span>Bills slowly increasing</span>
            </div>
            <div className="leak-source">
              <CheckCircle2 className="leak-source-icon" />
              <span>Duplicate services</span>
            </div>
            <div className="leak-source">
              <CheckCircle2 className="leak-source-icon" />
              <span>Credit card interest</span>
            </div>
            <div className="leak-source">
              <CheckCircle2 className="leak-source-icon" />
              <span>High card utilization</span>
            </div>
            <div className="leak-source">
              <CheckCircle2 className="leak-source-icon" />
              <span>Poor card usage decisions</span>
            </div>
            <div className="leak-source">
              <CheckCircle2 className="leak-source-icon" />
              <span>Missed financial reminders</span>
            </div>
          </div>
          
          <div className="watchdog-closing-line">
            <AlertCircle className="inline-icon-warning" />
            <p>Individually these seem small. Together, they can quietly drain hundreds or even thousands every year.</p>
          </div>
        </div>
      </section>

      {/* 360° Money Leak Detection Section */}
      <section className="watchdog-360-section">
        <div className="section-container">
          <div className="section-360-header">
            <Layers className="section-360-icon" />
            <h2 className="section-title">360° Money Leak Detection</h2>
          </div>
          
          <p className="section-description">
            Most financial tools focus on one narrow area.
          </p>
          
          <div className="narrow-tools-grid">
            <div className="narrow-tool">
              <span className="tool-text">Some track subscriptions.</span>
            </div>
            <div className="narrow-tool">
              <span className="tool-text">Some show spending charts.</span>
            </div>
            <div className="narrow-tool">
              <span className="tool-text">Some monitor credit scores.</span>
            </div>
            <div className="narrow-tool">
              <span className="tool-text">Some help with budgeting.</span>
            </div>
          </div>
          
          <p className="section-description-large">
            MySentinel brings these signals together into one continuous protection layer.
          </p>
          
          <div className="watches-for-grid">
            <div className="watches-item">
              <div className="watches-icon-wrapper">
                <Bell className="watches-icon" />
              </div>
              <span>Subscription leaks</span>
            </div>
            <div className="watches-item">
              <div className="watches-icon-wrapper">
                <Calendar className="watches-icon" />
              </div>
              <span>Trial-to-paid conversions</span>
            </div>
            <div className="watches-item">
              <div className="watches-icon-wrapper">
                <TrendingUp className="watches-icon" />
              </div>
              <span>Recurring bill increases</span>
            </div>
            <div className="watches-item">
              <div className="watches-icon-wrapper">
                <Layers className="watches-icon" />
              </div>
              <span>Duplicate services</span>
            </div>
            <div className="watches-item">
              <div className="watches-icon-wrapper">
                <DollarSign className="watches-icon" />
              </div>
              <span>Credit card interest leakage</span>
            </div>
            <div className="watches-item">
              <div className="watches-icon-wrapper">
                <BarChart3 className="watches-icon" />
              </div>
              <span>Credit utilization risk</span>
            </div>
            <div className="watches-item">
              <div className="watches-icon-wrapper">
                <Target className="watches-icon" />
              </div>
              <span>Smart card usage opportunities</span>
            </div>
            <div className="watches-item">
              <div className="watches-icon-wrapper">
                <RefreshCw className="watches-icon" />
              </div>
              <span>Renewal and financial admin reminders</span>
            </div>
          </div>
          
          <div className="watchdog-goal-message">
            <Shield className="goal-icon" />
            <div className="goal-text">
              <p>The goal is not just to show what happened.</p>
              <p className="goal-highlight">The goal is to detect what needs attention before it costs you money.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Continuous Monitoring Section */}
      <section className="watchdog-continuous-section">
        <div className="section-container">
          <h2 className="section-title">Financial Leaks Are Not a One-Time Problem</h2>
          <p className="section-description-large">
            Even financially disciplined people can:
          </p>
          
          <div className="discipline-challenges">
            <div className="challenge-box">
              <Calendar className="challenge-box-icon" />
              <span>Forget a trial</span>
            </div>
            <div className="challenge-box">
              <TrendingUp className="challenge-box-icon" />
              <span>Miss a bill increase</span>
            </div>
            <div className="challenge-box">
              <BarChart3 className="challenge-box-icon" />
              <span>Overlook utilization changes</span>
            </div>
            <div className="challenge-box">
              <Eye className="challenge-box-icon" />
              <span>Stop checking transactions</span>
            </div>
            <div className="challenge-box">
              <CreditCard className="challenge-box-icon" />
              <span>Use the wrong card</span>
            </div>
            <div className="challenge-box">
              <AlertCircle className="challenge-box-icon" />
              <span>Miss a recurring charge</span>
            </div>
          </div>
          
          <div className="continuous-message">
            <RefreshCw className="continuous-icon" />
            <div className="continuous-text">
              <p className="section-description">New leaks appear continuously.</p>
              <p className="section-description">Monitoring should be continuous too.</p>
              <p className="section-description-large">
                MySentinel runs in the background and alerts you when something needs action.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Proactive Assistant Section */}
      <section className="watchdog-assistant-section">
        <div className="section-container">
          <h2 className="section-title">Not a Dashboard. A Proactive Assistant.</h2>
          <p className="section-description">
            Traditional finance apps show charts and spending categories.
          </p>
          <p className="section-description-large">
            MySentinel focuses on action.
          </p>
          
          <div className="comparison-examples">
            <Card className="comparison-card">
              <CardContent className="comparison-content">
                <div className="comparison-instead">
                  <h4 className="comparison-label">Instead of:</h4>
                  <p className="comparison-text generic">&quot;You spent £220 on subscriptions.&quot;</p>
                </div>
                <div className="comparison-arrow">
                  <ArrowRight className="arrow-icon" />
                </div>
                <div className="comparison-mysentinel">
                  <h4 className="comparison-label">It tells you:</h4>
                  <p className="comparison-text actionable">&quot;Your Adobe trial renews in 2 days. Cancel now to avoid £29.99.&quot;</p>
                </div>
              </CardContent>
            </Card>

            <Card className="comparison-card">
              <CardContent className="comparison-content">
                <div className="comparison-instead">
                  <h4 className="comparison-label">Instead of:</h4>
                  <p className="comparison-text generic">&quot;Your card balance is high.&quot;</p>
                </div>
                <div className="comparison-arrow">
                  <ArrowRight className="arrow-icon" />
                </div>
                <div className="comparison-mysentinel">
                  <h4 className="comparison-label">It tells you:</h4>
                  <p className="comparison-text actionable">&quot;Card A is at 42% utilization. Consider using Card B or reducing Card A before statement close.&quot;</p>
                </div>
              </CardContent>
            </Card>

            <Card className="comparison-card">
              <CardContent className="comparison-content">
                <div className="comparison-instead">
                  <h4 className="comparison-label">Instead of:</h4>
                  <p className="comparison-text generic">&quot;Your broadband bill is £52.&quot;</p>
                </div>
                <div className="comparison-arrow">
                  <ArrowRight className="arrow-icon" />
                </div>
                <div className="comparison-mysentinel">
                  <h4 className="comparison-label">It tells you:</h4>
                  <p className="comparison-text actionable">&quot;Your broadband bill increased from £39 to £52. Estimated annual impact: £156.&quot;</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Watchdog Capabilities Section */}
      <section className="watchdog-capabilities-section">
        <div className="section-container">
          <h2 className="section-title">What MySentinel Watches For</h2>
          
          <div className="capabilities-grid">
            <Card className="capability-card">
              <CardContent className="capability-content">
                <Calendar className="capability-icon" />
                <h3 className="capability-title">Trial-to-Paid Alerts</h3>
                <p className="capability-description">
                  Get alerted before free trials turn into paid subscriptions.
                </p>
              </CardContent>
            </Card>

            <Card className="capability-card">
              <CardContent className="capability-content">
                <Bell className="capability-icon" />
                <h3 className="capability-title">Subscription Monitoring</h3>
                <p className="capability-description">
                  Find hidden subscriptions and recurring charges across accounts.
                </p>
              </CardContent>
            </Card>

            <Card className="capability-card">
              <CardContent className="capability-content">
                <TrendingUp className="capability-icon" />
                <h3 className="capability-title">Bill Increase Detection</h3>
                <p className="capability-description">
                  Spot when recurring bills or subscriptions quietly increase.
                </p>
              </CardContent>
            </Card>

            <Card className="capability-card">
              <CardContent className="capability-content">
                <BarChart3 className="capability-icon" />
                <h3 className="capability-title">Credit Utilization Alerts</h3>
                <p className="capability-description">
                  Know when card usage could create credit score risk.
                </p>
              </CardContent>
            </Card>

            <Card className="capability-card">
              <CardContent className="capability-content">
                <RefreshCw className="capability-icon" />
                <h3 className="capability-title">Balance Rebalancing Suggestions</h3>
                <p className="capability-description">
                  Get alerted when one card is high while another has unused room.
                </p>
              </CardContent>
            </Card>

            <Card className="capability-card">
              <CardContent className="capability-content">
                <DollarSign className="capability-icon" />
                <h3 className="capability-title">Interest Leakage Insights</h3>
                <p className="capability-description">
                  Identify unnecessary credit card interest and payment timing risks.
                </p>
              </CardContent>
            </Card>

            <Card className="capability-card">
              <CardContent className="capability-content">
                <Target className="capability-icon" />
                <h3 className="capability-title">Smart Card Guidance</h3>
                <p className="capability-description">
                  Know which card may be better to use based on utilization, interest risk, and rewards.
                </p>
              </CardContent>
            </Card>

            <Card className="capability-card">
              <CardContent className="capability-content">
                <Layers className="capability-icon" />
                <h3 className="capability-title">Duplicate Service Detection</h3>
                <p className="capability-description">
                  Detect overlapping services such as multiple streaming, storage, or productivity subscriptions.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Financial Impact Section */}
      <section className="watchdog-impact-section">
        <div className="section-container">
          <h2 className="section-title">Small Leaks Can Become Large Losses</h2>
          <p className="section-description">
            A few pounds here and there may not feel urgent.
          </p>
          <p className="section-description-large">
            But over a year, small leaks can become meaningful.
          </p>
          
          <Card className="impact-breakdown-card">
            <CardContent className="impact-breakdown-content">
              <h3 className="impact-breakdown-title">Example Annual Impact</h3>
              
              <div className="impact-items">
                <div className="impact-item">
                  <span className="impact-category">Forgotten subscriptions:</span>
                  <span className="impact-range">£120–£300/year</span>
                </div>
                <div className="impact-item">
                  <span className="impact-category">Bill increases:</span>
                  <span className="impact-range">£200–£500/year</span>
                </div>
                <div className="impact-item">
                  <span className="impact-category">Credit card interest leakage:</span>
                  <span className="impact-range">£300–£1,000+/year</span>
                </div>
                <div className="impact-item">
                  <span className="impact-category">Duplicate services:</span>
                  <span className="impact-range">£100–£250/year</span>
                </div>
              </div>
              
              <div className="impact-total-box">
                <span className="impact-total-label">Potential annual leaks detected:</span>
                <span className="impact-total-value">£300–£1,400+</span>
              </div>
              
              <p className="impact-disclaimer">
                Actual savings vary by user. These figures are illustrative examples based on common money leak categories.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Credit Protection Impact Section */}
      <section className="watchdog-credit-impact-section">
        <div className="section-container">
          <h2 className="section-title">Some Financial Mistakes Cost More Than Monthly Fees</h2>
          <p className="section-description">
            Subscription leaks are annoying.
          </p>
          <p className="section-description-large">
            But credit utilization and interest mistakes can have much larger long-term impact.
          </p>
          
          <p className="section-description">
            A weaker credit profile may affect borrowing costs for:
          </p>
          
          <div className="borrowing-types">
            <span className="borrowing-type">Mortgages</span>
            <span className="borrowing-type">Auto loans</span>
            <span className="borrowing-type">Personal loans</span>
            <span className="borrowing-type">Refinancing</span>
          </div>
          
          <Card className="credit-example-card">
            <CardContent className="credit-example-content">
              <h3 className="credit-example-title">Illustrative Example</h3>
              
              <div className="credit-details-grid">
                <div className="credit-detail">
                  <span className="credit-label">Mortgage Amount:</span>
                  <span className="credit-value">£300,000</span>
                </div>
                <div className="credit-detail">
                  <span className="credit-label">Term:</span>
                  <span className="credit-value">25 Years</span>
                </div>
              </div>
              
              <div className="credit-scenarios">
                <div className="credit-scenario">
                  <span className="scenario-label">Rate Scenario A:</span>
                  <span className="scenario-rate">4.0%</span>
                </div>
                <div className="credit-scenario">
                  <span className="scenario-label">Rate Scenario B:</span>
                  <span className="scenario-rate">4.5%</span>
                </div>
              </div>
              
              <div className="credit-impact-box">
                <span className="credit-impact-label">Potential Additional Interest:</span>
                <span className="credit-impact-value">£20,000–£30,000+</span>
              </div>
              
              <p className="credit-disclaimer">
                Illustrative example only. MySentinel does not guarantee credit score improvement, mortgage approval, or specific savings.
              </p>
            </CardContent>
          </Card>
          
          <div className="credit-closing-message">
            <Shield className="credit-message-icon" />
            <p className="credit-message-text">
              Protecting your credit profile is not just about today&apos;s score. It can affect tomorrow&apos;s borrowing power.
            </p>
          </div>
        </div>
      </section>

      {/* Monitor Everything Section */}
      <section className="watchdog-monitor-everything-section">
        <div className="section-container">
          <h2 className="section-title">Monitor Everything. Not Just One Account.</h2>
          <p className="section-description-large">
            Financial leaks rarely exist in a single place.
          </p>
          
          <p className="monitor-intro-text">
            Most people have multiple:
          </p>
          
          <div className="multiple-items-grid">
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Bank accounts</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Credit cards</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Subscriptions</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Recurring bills</span>
            </div>
            <div className="multiple-item">
              <CheckCircle2 className="multiple-icon" />
              <span>Service providers</span>
            </div>
          </div>
          
          <p className="monitor-pattern-text">
            Reviewing them individually makes it easy to miss important patterns.
          </p>
          
          <p className="section-description-large">
            MySentinel is designed to securely monitor your connected financial accounts together and identify 
            opportunities, risks, and money leaks across your entire financial ecosystem.
          </p>
          
          <div className="monitor-cards-grid">
            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <Activity className="monitor-card-icon" />
                <h3 className="monitor-card-title">Bank Accounts</h3>
                <p className="monitor-card-description">
                  Monitor multiple connected accounts in one place.
                </p>
              </CardContent>
            </Card>

            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <CreditCard className="monitor-card-icon" />
                <h3 className="monitor-card-title">Credit Cards</h3>
                <p className="monitor-card-description">
                  Track utilization, balances, and interest risks across cards.
                </p>
              </CardContent>
            </Card>

            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <Bell className="monitor-card-icon" />
                <h3 className="monitor-card-title">Subscriptions</h3>
                <p className="monitor-card-description">
                  Detect recurring charges and forgotten services.
                </p>
              </CardContent>
            </Card>

            <Card className="monitor-card">
              <CardContent className="monitor-card-content">
                <TrendingUp className="monitor-card-icon" />
                <h3 className="monitor-card-title">Bills</h3>
                <p className="monitor-card-description">
                  Monitor telecom, insurance, utilities, and other recurring expenses.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="monitor-highlight-box">
            <Shield className="monitor-highlight-icon" />
            <p className="monitor-highlight-text">Connect once. Monitor everything.</p>
          </div>
          
          <div className="monitor-supporting-content">
            <p className="section-description-large">
              The most valuable financial insights often appear when information is viewed across multiple 
              accounts rather than in isolation.
            </p>
            
            <h3 className="monitor-examples-title">Examples:</h3>
            <div className="monitor-examples">
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>One card approaching high utilization while another remains underused</span>
              </div>
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>Duplicate subscriptions across different payment methods</span>
              </div>
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>Rising recurring costs spread across multiple providers</span>
              </div>
              <div className="monitor-example">
                <ArrowRight className="monitor-example-icon" />
                <span>Hidden money leaks that are difficult to see when reviewing accounts individually</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="watchdog-how-section">
        <div className="section-container">
          <h2 className="section-title">How Continuous Protection Works</h2>
          
          <div className="how-steps">
            <div className="how-step">
              <div className="how-step-number">1</div>
              <div className="how-step-content">
                <Lock className="how-step-icon" />
                <h3 className="how-step-title">Connect securely</h3>
              </div>
            </div>

            <div className="how-connector">
              <ArrowRight className="how-connector-arrow" />
            </div>

            <div className="how-step">
              <div className="how-step-number">2</div>
              <div className="how-step-content">
                <Activity className="how-step-icon" />
                <h3 className="how-step-title">MySentinel continuously monitors financial activity for leaks and risks</h3>
              </div>
            </div>

            <div className="how-connector">
              <ArrowRight className="how-connector-arrow" />
            </div>

            <div className="how-step">
              <div className="how-step-number">3</div>
              <div className="how-step-content">
                <Bell className="how-step-icon" />
                <h3 className="how-step-title">Receive clear recommendations before small issues become costly</h3>
              </div>
            </div>
          </div>
          
          <div className="alert-channels">
            <h3 className="channels-title">Alert Channels:</h3>
            <div className="channels-grid">
              <div className="channel-item">
                <Bell className="channel-icon" />
                <span>Email alerts</span>
              </div>
              <div className="channel-item">
                <Zap className="channel-icon" />
                <span>Push notifications</span>
              </div>
              <div className="channel-item">
                <Calendar className="channel-icon" />
                <span>In-app reminders</span>
              </div>
            </div>
          </div>
          
          <div className="timing-examples">
            <h3 className="timing-title">Alert Examples:</h3>
            <div className="timing-grid">
              <div className="timing-item">
                <CheckCircle2 className="timing-icon" />
                <span>3 days before renewal</span>
              </div>
              <div className="timing-item">
                <CheckCircle2 className="timing-icon" />
                <span>Before statement close</span>
              </div>
              <div className="timing-item">
                <CheckCircle2 className="timing-icon" />
                <span>When a bill increases</span>
              </div>
              <div className="timing-item">
                <CheckCircle2 className="timing-icon" />
                <span>When a new recurring charge appears</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust and Privacy Section */}
      <section className="watchdog-trust-section">
        <div className="section-container">
          <h2 className="section-title">Built for Trust and Privacy</h2>
          <p className="section-description">
            Financial tools require a high level of trust.
          </p>
          <p className="section-description">
            This platform is designed with a privacy-first approach to ensure your financial data is secure and under your control.
          </p>
          
          <div className="trust-grid">
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Eye className="trust-icon" />
                </div>
                <h3 className="trust-card-title">Read-only Access</h3>
                <p className="trust-card-description">
                  We only analyze your financial data. We cannot move or modify your money.
                </p>
              </CardContent>
            </Card>
            
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Shield className="trust-icon" />
                </div>
                <h3 className="trust-card-title">No Money Movement</h3>
                <p className="trust-card-description">
                  The system does not initiate transactions or transfers.
                </p>
              </CardContent>
            </Card>
            
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Lock className="trust-icon" />
                </div>
                <h3 className="trust-card-title">No Data Selling</h3>
                <p className="trust-card-description">
                  Your personal financial data is never sold or shared with third parties.
                </p>
              </CardContent>
            </Card>
            
            <Card className="trust-card">
              <CardContent className="trust-card-content">
                <div className="trust-icon-wrapper">
                  <Zap className="trust-icon" />
                </div>
                <h3 className="trust-card-title">On-device Processing</h3>
                <p className="trust-card-description">
                  Sensitive insights are generated locally on your device to minimize data exposure (where possible).
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="trust-footer">
            <Shield className="trust-footer-icon" />
            <div className="trust-footer-text">
              <p>We only analyze financial activity to generate insights.</p>
              <p>We cannot move, withdraw, or transfer your money.</p>
              <p>Your financial data belongs to you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section className="watchdog-demo-section">
        <div className="section-container">
          <h2 className="section-title">Explore the Interactive Demo</h2>
          <p className="section-description">
            See how MySentinel detects financial leaks, highlights risks, and recommends timely actions.
          </p>
          
          <Button 
            onClick={() => handleDemoClick(FIGMA_DEMO_URL)}
            className="demo-cta-btn"
            size="lg"
          >
            Explore Interactive Demo
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="watchdog-waitlist" className="watchdog-waitlist-section">
        <div className="section-container">
          <div className="waitlist-header">
            <h2 className="section-title">Join the Waitlist</h2>
            <p className="waitlist-count">{waitlistCount.toLocaleString()} people waiting</p>
          </div>
          
          <div className="waitlist-form-container">
            {submitSuccess ? (
              <div className="success-message">
                <CheckCircle2 className="success-icon" />
                <h3>Thanks for joining!</h3>
                <p>You&apos;re on the list to help shape the proactive financial watchdog.</p>
              </div>
            ) : (
              <form onSubmit={handleWaitlistSubmit} className="waitlist-form">
                <div className="form-group">
                  <label htmlFor="watchdog-email" className="form-label">Email *</label>
                  <Input
                    id="watchdog-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="watchdog-country" className="form-label">Country (optional)</label>
                  <Input
                    id="watchdog-country"
                    type="text"
                    placeholder="e.g., United States"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group priority-problem-group">
                  <label className="form-label form-label-required">
                    What would make you sign up today? *
                  </label>
                  <p className="form-sublabel">
                    Select the one problem you would most like solved automatically.
                  </p>
                  <div className="radio-options">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="trial_reminder"
                        checked={priorityProblem === 'trial_reminder'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Never forget a free trial again</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="bill_increase"
                        checked={priorityProblem === 'bill_increase'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Catch bill increases automatically</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="credit_protection"
                        checked={priorityProblem === 'credit_protection'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Protect my credit score automatically</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="reduce_interest"
                        checked={priorityProblem === 'reduce_interest'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Reduce unnecessary credit card interest</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="find_subscriptions"
                        checked={priorityProblem === 'find_subscriptions'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Find hidden subscriptions and recurring charges</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="continuous_monitoring"
                        checked={priorityProblem === 'continuous_monitoring'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Continuously monitor my finances for money leaks</span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="priorityProblem"
                        value="other"
                        checked={priorityProblem === 'other'}
                        onChange={(e) => setPriorityProblem(e.target.value)}
                        required
                      />
                      <span className="radio-label">Other</span>
                    </label>
                    
                    {priorityProblem === 'other' && (
                      <div className="other-problem-input">
                        <Input
                          type="text"
                          placeholder="Please specify"
                          value={otherProblem}
                          onChange={(e) => setOtherProblem(e.target.value)}
                          required
                          className="form-input"
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="submit-btn"
                  size="lg"
                >
                  {isSubmitting ? 'Joining...' : 'Join Early Access'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Demo CTA After Waitlist */}
      <section className="watchdog-bottom-demo-section">
        <div className="section-container">
          <h2 className="section-title">Not Ready to Join Yet?</h2>
          <p className="section-description">
            See exactly how MySentinel works before deciding.
          </p>
          
          <Button 
            onClick={() => {
              if (window.posthog) {
                window.posthog.capture('bottom_demo_clicked_watchdog', {
                  source_page: '/watchdog'
                });
              }
              handleDemoClick(FIGMA_DEMO_URL);
            }}
            className="bottom-demo-btn"
            size="lg"
            variant="outline"
          >
            Try Interactive Demo
          </Button>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="watchdog-final-cta-section">
        <div className="section-container">
          <h2 className="final-cta-title">
            Let MySentinel Watch for Money Leaks So You Don&apos;t Have To
          </h2>
          <p className="final-cta-subtitle">
            Join early access and help shape a proactive financial watchdog that continuously monitors 
            hidden leaks, rising bills, unnecessary interest, and costly mistakes.
          </p>
          <div className="final-cta-buttons">
            <Button 
              onClick={() => document.getElementById('watchdog-waitlist')?.scrollIntoView({ behavior: 'smooth' })}
              className="final-cta-btn"
              size="lg"
            >
              Join Early Access
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => handleDemoClick(FIGMA_DEMO_URL)}
              variant="outline"
              className="final-demo-btn"
              size="lg"
            >
              Try Interactive Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Feedback Popup */}
      <FeedbackPopup
        isOpen={showFeedbackPopup}
        onClose={() => setShowFeedbackPopup(false)}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
};

export default WatchdogPage;
